import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import NotificationsIcon from '@mui/icons-material/Notifications';
import CheckIcon from '@mui/icons-material/Check';
import { useThemeMode } from '../../ThemeContext';
import { dataAPI } from '../../helpers/api_service';
import AppIconBlue from '../../assets/AppIconBlue.svg';
import '../../styles/loading-animations.css';

const Notifications = ({ showNotifications, setShowNotifications, notifications = [], onRefresh }) => {
  const { mode } = useThemeMode();
  const navigate = useNavigate();
  const [showLoading, setShowLoading] = useState(false);

  // Handle initial loading when sidebar opens for the first time
  useEffect(() => {
    if (showNotifications && notifications.length === 0) {
      setShowLoading(true);
      // Call parent to fetch notifications (only the first time sidebar opens)
      if (onRefresh) {
        onRefresh().finally(() => {
          setShowLoading(false);
        });
      } else {
        // Fallback timeout
        const timer = setTimeout(() => {
          setShowLoading(false);
        }, 1500);
        return () => clearTimeout(timer);
      }
    }
  }, [showNotifications]);

  // Clear loading when notifications are loaded
  useEffect(() => {
    if (showLoading && notifications.length > 0) {
      setShowLoading(false);
    }
  }, [showLoading, notifications.length]);

  // Handle notification click with smart navigation
  const handleNotificationClick = async (notification) => {
    console.log('🎯 Notification clicked:', notification);
    console.log('🔍 Related entity ID:', notification.related_entity_id);
    console.log('🔍 Related entity type:', notification.related_entity_type);
    console.log('🔍 Project ID:', notification.project_id);
    
    // Mark notification as read if it's unread
    if (!notification.is_read) {
      try {
        console.log('📬 Marking notification as read on click:', notification.id);
        // Disable global loading indicator for this API call to avoid popup overlay
        const response = await dataAPI.markNotificationRead(notification.id, { showLoading: false });
        
        if (response.status === 'success') {
          console.log('✅ Notification marked as read successfully');
          
          // Refetch notifications to update the badge (also without global loading)
          if (onRefresh) {
            await onRefresh();
          }
        }
      } catch (error) {
        console.error('❌ Error marking notification as read:', error);
        // Continue with navigation even if marking as read fails
      }
    }
    
    // Check if notification has related entity information
    if (notification.related_entity_id && notification.related_entity_type) {
      
      switch (notification.related_entity_type.toLowerCase()) {
        case 'task':
          // Navigate to task details page
          // Since notification API doesn't provide project_id, we'll use a fallback approach
          console.log(`📋 Navigating to task: ${notification.related_entity_id}`);
          
          // Try to navigate to task - TaskDetails will fetch the project_id from API
          // Use project ID 1 as fallback - TaskDetails will get the real project_id from API response
          navigate(`/project/1/task/${notification.related_entity_id}`, {
            state: {
              fromNotification: true,
              notificationId: notification.id
            }
          });
          break;
          
        case 'project':
        case 'projects':
          // Navigate to projects page (or specific project if needed)
          console.log(`📁 Navigating to projects page`);
          navigate('/projects', {
            state: {
              fromNotification: true,
              notificationId: notification.id,
              focusProjectId: notification.related_entity_id
            }
          });
          break;
          
        case 'document':
          // Navigate to documents page
          console.log(`📄 Navigating to documents page`);
          navigate('/documents', {
            state: {
              fromNotification: true,
              notificationId: notification.id,
              focusDocumentId: notification.related_entity_id // Highlight this document if needed
            }
          });
          break;
          
        default:
          // For other types, show alert and don't navigate
          console.log(`❓ Unknown notification type: ${notification.related_entity_type}`);
          alert(`Notification Type: ${notification.related_entity_type}\n\nThis notification type is not yet clickable.\n\nTitle: ${notification.title}\nID: ${notification.id}`);
          return;
      }
      
      // Close notification sidebar after successful navigation
      setShowNotifications(false);
      
    } else {
      // No related entity info - show alert
      console.log(`📭 Notification has no related entity:`, notification);
      alert(`Notification Info\n\nTitle: ${notification.title}\nMessage: ${notification.message}\nID: ${notification.id}\nTime: ${notification.sent_time}`);
    }
  };

  // Format relative time
  const formatRelativeTime = (sentTime) => {
    const now = new Date();
    const sent = new Date(sentTime);
    const diffMs = now - sent;
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffWeeks = Math.floor(diffDays / 7);

    // Less than 1 minute
    if (diffMinutes < 1) {
      return 'Just now';
    }
    // 1-59 minutes ago
    else if (diffMinutes < 60) {
      return diffMinutes === 1 ? '1 minute ago' : `${diffMinutes} minutes ago`;
    }
    // 1-23 hours ago
    else if (diffHours < 24) {
      return diffHours === 1 ? '1 hour ago' : `${diffHours} hours ago`;
    }
    // 1-6 days ago
    else if (diffDays < 7) {
      return diffDays === 1 ? '1 day ago' : `${diffDays} days ago`;
    }
    // 1 week ago (7-13 days)
    else if (diffWeeks === 1) {
      return '1 week ago';
    }
    // More than 1 week - show full date and time
    else {
      return `${sent.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      })} ${sent.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      })}`;
    }
  };

  // Handle marking notification as read
  const handleMarkAsRead = async (notificationId, e) => {
    // Stop event propagation to prevent triggering notification click
    e.preventDefault();
    e.stopPropagation();
    
    console.log('📬 Marking notification as read:', notificationId);
    
    try {
      // Call the mark as read API without global loading indicator
      const response = await dataAPI.markNotificationRead(notificationId, { showLoading: false });
      
      if (response.status === 'success') {
        console.log('✅ Notification marked as read successfully');
        
        // Refetch notifications to update the badge
        if (onRefresh) {
          await onRefresh();
        }
      } else {
        console.error('❌ Failed to mark notification as read:', response.message);
      }
    } catch (error) {
      console.error('❌ Error marking notification as read:', error);
    }
  };

  // Handle mark all notifications as read
  const handleMarkAllAsRead = async () => {
    if (!notifications.some(n => !n.is_read)) return;
    setShowLoading(true);
    try {
      const response = await dataAPI.markAllNotificationsRead({ showLoading: false });
      if (response.status === 'success' && onRefresh) {
        await onRefresh();
      }
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    } finally {
      setShowLoading(false);
    }
  };

  // Handle refresh notifications
  const handleRefreshNotifications = async () => {
    console.log('🔄 Starting LOCAL notification refresh (no global loading)...');
    setShowLoading(true);
    
    try {
      // Call the parent's refresh function (uses showLoading: false)
      if (onRefresh) {
        console.log('📡 Calling parent refresh API...');
        await onRefresh();
        console.log('✅ Notifications refreshed successfully!');
        
        // Add small delay to ensure loading overlay is visible
        await new Promise(resolve => setTimeout(resolve, 500));
      } else {
        console.warn('⚠️ onRefresh callback not provided');
        // Simulate API delay
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
    } catch (error) {
      console.error('❌ Error refreshing notifications:', error);
    } finally {
      setShowLoading(false);
      console.log('🏁 LOCAL loading overlay hidden - refresh completed');
    }
  };

  return (
    <>
      <style>
        {`
          /* Hide scrollbar for notification content */
          .notifications-content::-webkit-scrollbar {
            display: none;
          }
        `}
      </style>
      {/* Notifications Right Sidebar */}
      {showNotifications && (
        <>
          {/* Backdrop */}
          <div 
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 999,
              animation: 'fadeIn 0.3s ease-out'
            }}
            onClick={(e) => {
              // Only close if clicking on the backdrop itself, not on child elements
              if (e.target === e.currentTarget) {
                setShowNotifications(false);
              }
            }}
            onMouseDown={(e) => e.stopPropagation()}
          />
          
          {/* Sidebar */}
          <div 
            style={{
              position: 'fixed',
              top: 0,
              right: 0,
              width: '100%',
              maxWidth: '400px',
              height: '100vh',
              backgroundColor: mode === 'dark' ? '#2a2a2a' : '#ffffff',
              borderLeft: `1px solid ${mode === 'dark' ? '#444' : '#e0e0e0'}`,
              boxShadow: '-4px 0 12px rgba(0,0,0,0.15)',
              zIndex: 1000,
              display: 'flex',
              flexDirection: 'column',
              animation: 'slideInRight 0.3s ease-out'
            }}
            onClick={(e) => {
              // Stop propagation from sidebar to avoid trigger backdrop close
              e.stopPropagation();
            }}
          >
            {/* Header */}
            <div 
              style={{
                padding: '20px',
                borderBottom: `1px solid ${mode === 'dark' ? '#444' : '#e0e0e0'}`,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexShrink: 0
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <h2 style={{
                margin: 0,
                fontSize: '18px',
                fontWeight: '600',
                color: mode === 'dark' ? '#ffffff' : '#333333'
              }}>
                Notifications ({notifications.length})
              </h2>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                {notifications.some(n => !n.is_read) && (
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleMarkAllAsRead();
                    }}
                    style={{
                      background: mode === 'dark' ? '#26c3c0' : '#26c3c0',
                      border: 'none',
                      borderRadius: '6px',
                      padding: '6px 12px',
                      fontSize: '13px',
                      fontWeight: '500',
                      color: '#fff',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      whiteSpace: 'nowrap'
                    }}
                    onMouseDown={(e) => e.stopPropagation()}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#22b0ad';
                      e.currentTarget.style.opacity = '1';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#26c3c0';
                      e.currentTarget.style.opacity = '1';
                    }}
                    title="Mark all as read"
                  >
                    <CheckIcon style={{ fontSize: '16px' }} />
                    Mark read all
                  </button>
                )}
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleRefreshNotifications();
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '16px',
                    cursor: 'pointer',
                    color: mode === 'dark' ? '#26c3c0' : '#26c3c0',
                    padding: '4px',
                    borderRadius: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    userSelect: 'none'
                  }}
                  onMouseDown={(e) => {
                    e.stopPropagation();
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = mode === 'dark' ? '#444' : '#f0f0f0';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'transparent';
                  }}
                >
                  ↻
                </button>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    console.log('Close button clicked!');
                    setShowNotifications(false);
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '24px',
                    cursor: 'pointer',
                    color: mode === 'dark' ? '#ffffff' : '#333333',
                    padding: '4px',
                    borderRadius: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    userSelect: 'none'
                  }}
                  onMouseDown={(e) => {
                    e.stopPropagation();
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = mode === 'dark' ? '#444' : '#f0f0f0';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'transparent';
                  }}
                >
                  ×
                </button>
              </div>
            </div>
            
            {/* Content */}
            <div className="notifications-content" style={{
              flex: 1,
              overflowY: 'auto',
              padding: '20px',
              position: 'relative',
              scrollbarWidth: 'none', /* Firefox */
              msOverflowStyle: 'none', /* IE and Edge */
              WebkitScrollbar: 'none' /* Chrome, Safari, Opera */
            }}>
              {/* Notifications Loading Overlay */}
              {showLoading && (
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  zIndex: 1003,
                  backgroundColor: mode === 'dark' ? 'rgba(0, 0, 0, 0.7)' : 'rgba(255, 255, 255, 0.8)',
                  backdropFilter: 'blur(4px)',
                  pointerEvents: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '12px'
                }}>
                  {/* Fill Animation Loading Indicator */}
                  <div className="fill-animation-container">
                    {/* Background container */}
                    <div className="fill-animation-background" />
                    
                    {/* Progress fill animation */}
                    <div className="fill-animation-progress" />
                    
                    {/* AppIconBlue with pulse animation */}
                    <img 
                      src={AppIconBlue} 
                      alt="Loading" 
                      className={`fill-animation-icon pulse-animation-icon`}
                    />
                  </div>
                </div>
              )}

              {notifications.length === 0 ? (
                <div style={{ 
                  fontSize: '14px', 
                  color: mode === 'dark' ? '#ccc' : '#666',
                  textAlign: 'center',
                  padding: '40px 20px',
                  fontStyle: 'italic'
                }}>
                  No notifications found
                </div>
              ) : (
                notifications.map((notification, index) => {
                  // Check if notification has known clickable type
                  const isClickable = notification.related_entity_id && notification.related_entity_type && 
                    ['task', 'project', 'projects', 'document'].includes(notification.related_entity_type.toLowerCase());
                  
                  return (
                    <div 
                      key={notification.id}
                      style={{ 
                        marginBottom: index < notifications.length - 1 ? '16px' : '0',
                        padding: '16px',
                        backgroundColor: !notification.is_read ? (mode === 'dark' ? '#333' : '#f8f9fa') : 'transparent',
                        borderRadius: '8px',
                        borderLeft: !notification.is_read ? `4px solid #26c3c0` : 'none',
                        transition: 'background-color 0.2s ease',
                        cursor: isClickable ? 'pointer' : 'default',
                        userSelect: 'none'
                    }}
                    onClick={isClickable ? (e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      console.log('Notification card clicked:', notification.title);
                      handleNotificationClick(notification);
                   } : undefined}
                   onMouseDown={isClickable ? (e) => {
                     e.stopPropagation();
                   } : undefined}
                   onMouseEnter={isClickable ? (e) => {
                     e.currentTarget.style.backgroundColor = mode === 'dark' ? '#444' : '#f0f0f0';
                   } : undefined}
                   onMouseLeave={isClickable ? (e) => {
                     e.currentTarget.style.backgroundColor = !notification.is_read ? (mode === 'dark' ? '#333' : '#f8f9fa') : 'transparent';
                   } : undefined}
                  >
                    <div style={{ 
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '8px',
                      marginBottom: '8px'
                    }}>
                      <NotificationsIcon 
                        style={{ 
                          fontSize: '16px',
                          color: '#26c3c0',
                          marginTop: '2px',
                          flexShrink: 0
                        }} 
                      />
                      <div style={{ 
                        fontWeight: '600',
                        fontSize: '14px',
                        color: mode === 'dark' ? '#fff' : '#333',
                        flex: 1
                      }}>
                        {notification.title}
                      </div>
                      {/* Mark as Read Button - Only show if notification is unread */}
                      {!notification.is_read && (
                        <button
                          onClick={(e) => handleMarkAsRead(notification.id, e)}
                          onMouseDown={(e) => e.stopPropagation()}
                          style={{
                            background: '#26c3c0',
                            border: 'none',
                            borderRadius: '4px',
                            padding: '4px 8px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            fontSize: '11px',
                            color: '#fff',
                            fontWeight: '500',
                            flexShrink: 0,
                            transition: 'background-color 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.backgroundColor = '#22b0ad';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.backgroundColor = '#26c3c0';
                          }}
                          title="Mark as read"
                        >
                          <CheckIcon style={{ fontSize: '14px' }} />
                          Read
                        </button>
                      )}
                    </div>
                    <div style={{ 
                      fontSize: '13px',
                      color: mode === 'dark' ? '#ccc' : '#666',
                      marginBottom: '8px',
                      lineHeight: '1.5',
                      marginLeft: '24px',
                      wordBreak: 'break-all',
                      overflowWrap: 'break-word',
                      whiteSpace: 'normal'
                    }}>
                      {notification.message}
                    </div>
                    <div style={{ 
                      fontSize: '11px',
                      color: mode === 'dark' ? '#888' : '#999',
                      marginLeft: '24px'
                    }}>
                      {formatRelativeTime(notification.sent_time)}
                    </div>
                   </div>
                   );
                 })
               )}
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default Notifications;